<script lang="ts" setup>
import { ref, watch } from 'vue'

import {
  CricketSubParticipantStatId,
  Participant,
} from '@collector/sportsapi-client-legacy'
import UpdateEventSubParticipants from '@mobile/ActionQueue/Actions/Event/UpdateEventSubParticipants'
import Button from '@mobile/components/Button/Button.vue'
import { EventSettingsType } from '@mobile/database/EventSettingsRepository'
import { getTeamSide } from '@mobile/reusables/entityUtils'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useEventSettings } from '@mobile/views/Relation/useEventSettings'

import { useCricketCurrentBattersAndBowler } from '../../useCricketCurrentBattersAndBowler'
import LiveViewSubParticipant from './LiveViewSubParticipant.vue'
import { prepareUpdateEventSubParticipantsPayload } from './utils'

interface Props {
  dir: 'ltr' | 'rtl'
  participant: Participant
}

const props = defineProps<Props>()

// composables
const { probableEvent, actionQueue, probableHomeLineups, probableAwayLineups } =
  useEventInjections()
const { eventSettings, updateSetting } = useEventSettings(
  EventSettingsType.MOBILE,
)
const { currentMainBatter } = useCricketCurrentBattersAndBowler(
  ref(getTeamSide(probableEvent.value.participants, props.participant.id)),
)

// data
const side = getTeamSide(probableEvent.value.participants, props.participant.id)
const currentParticipantLineups =
  side === 'home' ? probableHomeLineups : probableAwayLineups

// methods
function changeCurrentBatter(): void {
  if (!eventSettings.value.currentSelectedCachedBatterId) {
    return
  }

  actionQueue.value.add(
    new UpdateEventSubParticipants(
      prepareUpdateEventSubParticipantsPayload(
        CricketSubParticipantStatId.CurrentBatter,
        eventSettings.value.currentSelectedCachedBatterId,
        currentParticipantLineups.value,
      ),
    ),
  )

  changeCachedBatterOnSubParticipantsUpdate()
}

function changeCachedBatterOnSubParticipantsUpdate(): void {
  watch(
    currentMainBatter,
    () => {
      updateSetting(
        'currentSelectedCachedBatterId',
        currentMainBatter.value?.participant_id,
      )
    },
    { deep: true, once: true },
  )
}
</script>

<template>
  <div class="flex flex-row gap-2 lg:flex-col">
    <div class="flex-1">
      <LiveViewSubParticipant
        :participant
        :dir
        type="main"
        batter
      />
      <LiveViewSubParticipant
        :participant
        :dir
        type="sub"
        batter
      />
    </div>

    <Button
      class="h-[88px] w-[100px] lg:h-16 lg:w-full"
      color="blue"
      size="md"
      :disabled="!eventSettings.currentSelectedCachedBatterId"
      @click="changeCurrentBatter"
    >
      <div class="grid content-center">
        <div class="flex flex-col items-center gap-2 lg:flex-row">
          <FontAwesomeIcon icon="exchange-alt" />
          <div>Change</div>
        </div>
      </div>
    </Button>
  </div>
</template>
